import React from 'react';
import { getStatusConfig } from '../constants';
import { ApplicationLearningSummary } from '../types';

interface StatPill {
  color: string;
  label: string;
}

const getDaysPlural = (n: number) => {
  const absN = Math.abs(n);
  if (absN % 10 === 1 && absN % 100 !== 11) return 'день';
  if (absN % 10 >= 2 && absN % 10 <= 4 && (absN % 100 < 10 || absN % 100 >= 20)) return 'дня';
  return 'дней';
};

function buildStatPills(item: ApplicationLearningSummary): StatPill[] {
  const pills: StatPill[] = [];
  if (item.overall_status === 'at_risk') {
    if (item.completed_count > 0) pills.push({ color: 'bg-[#33C000]', label: `${item.completed_count} завершили` });
    if (item.at_risk_count > 0)   pills.push({ color: 'bg-[#EF7744]', label: `${item.at_risk_count} в зоне риска` });
  } else if (item.overall_status === 'overdue') {
    if (item.completed_count > 0) pills.push({ color: 'bg-[#33C000]', label: `${item.completed_count} завершили` });
    if (item.overdue_count > 0)   pills.push({ color: 'bg-[#EF4444]', label: `${item.overdue_count} просрочили` });
  } else {
    if (item.no_activity_count > 0) pills.push({ color: 'bg-[#D9D9D9]', label: `${item.no_activity_count} без активности` });
    if (item.completed_count > 0)   pills.push({ color: 'bg-[#33C000]', label: `${item.completed_count} завершили` });
    if (item.at_risk_count > 0)     pills.push({ color: 'bg-[#EF7744]', label: `${item.at_risk_count} в зоне риска` });
    if (item.overdue_count > 0)     pills.push({ color: 'bg-[#EF4444]', label: `${item.overdue_count} просрочили` });
  }
  return pills;
}

const ApplicationCard: React.FC<{
  item: ApplicationLearningSummary;
  onDetails: (id: number) => void;
  hideDetails?: boolean;
}> = ({ item, onDetails, hideDetails = false }) => {
  const cfg = getStatusConfig(item.overall_status);
  const pills = buildStatPills(item);

  const completedPct = item.total_enrollments > 0
    ? Math.round((item.completed_count / item.total_enrollments) * 100)
    : 0;

  const deptLogo = item.departments.length > 0 ? item.departments[0] : null;

  const verticalTrimStyle = {
    leadingTrim: 'both',
    textEdge: 'cap',
    display: 'flex',
    alignItems: 'center',
  } as React.CSSProperties;

  const renderDeadlineText = () => {
    if (item.days_until_nearest_deadline === null) return null;
    const absDays = Math.abs(item.days_until_nearest_deadline);
    const daysText = getDaysPlural(absDays);
    if (item.overall_status === 'overdue') {
      return <span className="font-normal text-[15px] leading-[18px] text-[#EF4444] text-right">Просрочено на {absDays} {daysText}</span>;
    }
    if (item.overall_status === 'at_risk') {
      return <span className="font-normal text-[15px] leading-[18px] text-[#EF7744] text-right">Крайний срок через {absDays} {daysText}</span>;
    }
    return null;
  };

  const specialistInfo = item.specialist_full_name
    ? `${item.specialist_full_name}${item.specialist_email ? ` (${item.specialist_email})` : ''}`
    : 'Ответственный не назначен';

  return (
    <div className="bg-[#FFFFFF] border border-[#E5E7EB] rounded-[10px] p-5 flex flex-col gap-6">

      {/* Верхний блок */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          {deptLogo && (
            <div className="font-semibold text-[32px] leading-[39px] text-[#1E61AF] uppercase">
              {deptLogo}
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <h3 className="font-semibold text-[18px] leading-[22px] text-[#1F2937]" style={verticalTrimStyle}>
              {item.organization_short_name || 'Название организации не найдено'}
            </h3>
            <p className="font-normal text-[15px] leading-[18px] text-[#4B5563]" style={verticalTrimStyle}>
              id-{item.application_id} — {specialistInfo}
            </p>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <span className={`px-[16px] py-1.5 rounded-[8px] text-[11px] font-semibold uppercase tracking-wider ${cfg.badgeBg} ${cfg.badgeText}`}>
            {cfg.label}
          </span>
          {renderDeadlineText()}
        </div>
      </div>

      {/* Нижний блок */}
      <div className="flex items-center justify-between gap-4 pt-2 border-t border-[#F3F4F6]">
        {/* Статусы (Pills) */}
        <div className="flex gap-5 items-center flex-wrap">
          {pills.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-[4px] ${p.color}`} />
              <span className="text-sm text-[#4B5563]">{p.label}</span>
            </div>
          ))}
        </div>

        {/* Прогресс и Кнопка */}
        <div className="flex items-center gap-5 shrink-0">
          <span className="text-sm text-[#4B5563] whitespace-nowrap">
            Прогресс обучения по заявке:{' '}
            <span className="font-semibold text-[#111827]">
              {item.completed_count}/{item.total_enrollments} ({completedPct}%)
            </span>
          </span>

          {!hideDetails && (
            <button
              onClick={() => onDetails(item.application_id)}
              className="px-6 py-2 bg-[#1E61AF] hover:bg-[#164E8D] text-white font-semibold text-[14px] leading-[17px] rounded-[10px] transition-colors whitespace-nowrap"
            >
              Подробнее
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationCard;